// src/pages/KhatabookPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaArrowDown,
  FaArrowUp,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import { formatStoredMoney } from "../utils/currency";
import { API_BASE_URL } from "../config/constants";
import { cachePosMasterData, getCachedPosMasterData } from "../utils/posMasterDataCache";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { listOfflineParties } from "../offline/partyStore";
import {
  addOfflineKhatabookEntry,
  deleteOfflineKhatabookEntry,
  getOfflineKhatabook,
  getOfflineKhatabookOverall,
} from "../offline/khatabookStore";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  red: "#dc2626",
};

const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const getApiError = (json, fallback) => {
  const validationError = json?.errors
    ? Object.values(json.errors).flat().find(Boolean)
    : null;
  return validationError || json?.message || fallback;
};

const formatEntryDate = (entry) => {
  const value = entry?.entry_date || entry?.created_at || entry?.date;
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const KhatabookPage = () => {
  const [userId, setUserId] = useState(null);
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedParty, setSelectedParty] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);
  const [overall, setOverall] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [amount, setAmount] = useState("");
  const [flow, setFlow] = useState("in"); // "in" or "out"
  const [saving, setSaving] = useState(false);
  const ledgerRequestId = useRef(0);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  const fetchOverall = useCallback(async () => {
    try {
      if (isFullOfflineModeEnabled()) {
        const result = await getOfflineKhatabookOverall(userId);
        setOverall(result?.overall_summary || null);
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/khatabook/get-overall-summary`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(getApiError(json, "Failed to load summary"));
      }
      setOverall(json?.overall_summary || null);
    } catch (e) {
      toast.error(e.message || "Unable to load khatabook summary");
    }
  }, [userId]);

  const fetchParties = useCallback(async () => {
    if (isFullOfflineModeEnabled()) {
      const list = await listOfflineParties(userId, true);
      setParties(Array.isArray(list) ? list : []);
      return;
    }
    const cached = await getCachedPosMasterData(userId, "parties");
    const hasCachedParties = Array.isArray(cached?.records) && cached.records.length > 0;
    if (hasCachedParties) setParties(cached.records);
    try {
      const res = await fetch(
        `${API_BASE_URL}/list/partylistformaster`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(getApiError(json, "Failed to load parties"));
      }
      const list = json?.list || [];
      setParties(list);
      if (Array.isArray(list)) await cachePosMasterData(userId, "parties", list);
    } catch (e) {
      if (hasCachedParties) toast.info("Offline mode — showing saved customers.");
      else toast.error(e.message || "Unable to load parties");
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    let active = true;
    const bootstrap = async () => {
      await Promise.allSettled([fetchParties(), fetchOverall()]);
      if (active) setLoading(false);
    };
    bootstrap();

    return () => {
      active = false;
    };
  }, [fetchOverall, fetchParties, userId]);

  const fetchLedger = async (partyId) => {
    const requestId = ++ledgerRequestId.current;
    const party = parties.find((item) => String(item.id) === String(partyId));
    setSelectedParty(party || null);
    setLedger([]);
    setSummary(null);
    try {
      setRefreshing(true);
      if (isFullOfflineModeEnabled()) {
        const result = await getOfflineKhatabook(userId, partyId);
        if (requestId !== ledgerRequestId.current) return;
        setLedger(result?.entries || []);
        setSummary(result?.summary || null);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/khatabook/get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ party_id: partyId, restaurantid: userId }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(getApiError(json, "Failed to load khatabook"));
      }
      if (requestId !== ledgerRequestId.current) return;
      setLedger(json?.entries || []);
      setSummary(json?.summary || null);
    } catch (e) {
      if (requestId === ledgerRequestId.current) {
        toast.error(e.message || "Unable to load khatabook");
      }
    } finally {
      if (requestId === ledgerRequestId.current) setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parties;
    return parties.filter(
      (p) =>
        p.partyname?.toLowerCase().includes(q) ||
        String(p.partycontactpersonmobile || "").includes(q)
    );
  }, [parties, search]);

  const pickNumber = (obj, keys, fallback = 0) => {
    if (!obj) return fallback;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) {
        const n = Number(obj[k]);
        if (!Number.isNaN(n)) return n;
      }
    }
    return fallback;
  };

  const handleAddEntry = async () => {
    if (!selectedParty?.id) return toast.info("Select a party first");
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return toast.info("Enter a valid amount");
    }
    const endpoint =
      flow === "in"
        ? `${API_BASE_URL}/khatabook/add-money-in`
        : `${API_BASE_URL}/khatabook/add-money-out`;
    try {
      setSaving(true);
      if (isFullOfflineModeEnabled()) {
        await addOfflineKhatabookEntry(
          userId,
          selectedParty.id,
          flow,
          numericAmount
        );
        toast.success(`Money ${flow === "in" ? "received" : "given"}`);
        setAmount("");
        await Promise.all([fetchLedger(selectedParty.id), fetchOverall()]);
        return;
      }
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          party_id: selectedParty.id,
          restaurantid: userId,
          amount: numericAmount,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(getApiError(json, "Failed to add entry"));
      }
      toast.success(`Money ${flow === "in" ? "received" : "given"}`);
      setAmount("");
      await Promise.all([fetchLedger(selectedParty.id), fetchOverall()]);
    } catch (e) {
      toast.error(e.message || "Unable to add entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      if (isFullOfflineModeEnabled()) {
        await deleteOfflineKhatabookEntry(userId, entryId);
        toast.success("Deleted");
        await Promise.all([fetchLedger(selectedParty.id), fetchOverall()]);
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/khatabook/delete-entry`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entry_id: entryId, restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(getApiError(json, "Failed to delete"));
      }
      toast.success("Deleted");
      setLedger((prev) => prev.filter((e) => e.id !== entryId));
      await Promise.all([fetchLedger(selectedParty.id), fetchOverall()]);
    } catch (e) {
      toast.error(e.message || "Unable to delete");
    }
  };

  const overallBalance = pickNumber(overall, ["balance", "total_balance", "net"]);
  const selectedBalance = pickNumber(summary, ["balance", "total_balance", "net"]);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <TopStrip>
        <HeroCopy>
          <Eyebrow>Accounts & payments</Eyebrow>
          <StripTitle>Khatabook</StripTitle>
          <StripSub>Every party balance and payment, all in one place.</StripSub>
        </HeroCopy>
        <TopStats>
          <TopStat>
            <StatIcon><FaUsers /></StatIcon>
            <StatCopy>
              <small>Total parties</small>
              <strong>{overall?.total_parties || parties.length || 0}</strong>
            </StatCopy>
          </TopStat>
          <TopStat $tone="green">
            <StatIcon><FaArrowDown /></StatIcon>
            <StatCopy>
              <small>Total received</small>
              <strong>{fmtINR(pickNumber(overall, ["total_in", "total_money_in"]))}</strong>
            </StatCopy>
          </TopStat>
          <TopStat $tone="red">
            <StatIcon><FaArrowUp /></StatIcon>
            <StatCopy>
              <small>Total paid</small>
              <strong>{fmtINR(pickNumber(overall, ["total_out", "total_money_out"]))}</strong>
            </StatCopy>
          </TopStat>
          <TopStat $tone={overallBalance < 0 ? "red" : "green"}>
            <StatIcon><FaWallet /></StatIcon>
            <StatCopy>
              <small>Net balance</small>
              <strong>{fmtINR(overallBalance)}</strong>
            </StatCopy>
          </TopStat>
        </TopStats>
      </TopStrip>

      <Grid>
        <Card $sidebar>
          <SidebarHead>
            <TitleRow>
              <CardTitle>Parties</CardTitle>
              <CountBadge>{filtered.length}</CountBadge>
            </TitleRow>
            <CardSub>Select a party to view their ledger</CardSub>
            <SearchBox>
              <FaSearch aria-hidden="true" />
              <input
                placeholder="Search by name or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>
          </SidebarHead>
          {loading ? (
            <EmptyState>Loading parties…</EmptyState>
          ) : (
            <PartyList>
              {filtered.map((p) => (
                <PartyRow
                  key={p.id}
                  $active={selectedParty?.id === p.id}
                  onClick={() => fetchLedger(p.id)}
                >
                  <div className="left">
                    <Avatar>{(p.partyname || "?").charAt(0).toUpperCase()}</Avatar>
                    <PartyInfo>
                      <div className="name">{p.partyname}</div>
                      <div className="sub">
                        <span>{p.partycontactpersonmobile || "No mobile"}</span>
                        {(p.type || p.partytype) && <PartyType>{p.type || p.partytype}</PartyType>}
                      </div>
                    </PartyInfo>
                  </div>
                  <Arrow>›</Arrow>
                </PartyRow>
              ))}
              {!filtered.length && <EmptyState>No matching parties found</EmptyState>}
            </PartyList>
          )}
        </Card>

        <Card $ledger>
          <CardHead>
            <LedgerIdentity>
              <LedgerAvatar>{selectedParty ? (selectedParty.partyname || "?").charAt(0).toUpperCase() : <FaWallet />}</LedgerAvatar>
              <div>
                <CardTitle>Ledger</CardTitle>
                <CardSub>
                  {selectedParty ? selectedParty.partyname : "Choose a party from the list"}
                </CardSub>
              </div>
            </LedgerIdentity>
            <RefreshBtn
              onClick={() => selectedParty && fetchLedger(selectedParty.id)}
              disabled={!selectedParty || refreshing}
            >
              <FaSyncAlt className={refreshing ? "spinning" : ""} />
              {refreshing ? "Refreshing" : "Refresh"}
            </RefreshBtn>
          </CardHead>

          {summary ? (
            <SummaryRow>
              <SummaryCard $tone={selectedBalance < 0 ? "red" : "neutral"}>
                <SummaryIcon><FaWallet /></SummaryIcon>
                <div>
                  <small>Current balance</small>
                  <strong>{fmtINR(selectedBalance)}</strong>
                </div>
              </SummaryCard>
              <SummaryCard $tone="green">
                <SummaryIcon><FaArrowDown /></SummaryIcon>
                <div>
                  <small>Money in</small>
                  <strong>
                    {fmtINR(pickNumber(summary, ["total_in", "total_money_in", "money_in"]))}
                  </strong>
                </div>
              </SummaryCard>
              <SummaryCard $tone="red">
                <SummaryIcon><FaArrowUp /></SummaryIcon>
                <div>
                  <small>Money out</small>
                  <strong>
                    {fmtINR(pickNumber(summary, ["total_out", "total_money_out", "money_out"]))}
                  </strong>
                </div>
              </SummaryCard>
            </SummaryRow>
          ) : null}

          {selectedParty ? (
            <>
              <EntryPanel>
                <EntryPanelHead>
                  <strong>Add transaction</strong>
                  <span>Record a payment for {selectedParty.partyname}</span>
                </EntryPanelHead>
                <AddRow>
                  <FlowTabs>
                    <FlowTab $active={flow === "in"} $tone="green" onClick={() => setFlow("in")}>
                      <FaArrowDown /> Money in
                    </FlowTab>
                    <FlowTab $active={flow === "out"} $tone="red" onClick={() => setFlow("out")}>
                      <FaArrowUp /> Money out
                    </FlowTab>
                  </FlowTabs>
                  <AmountField>
                    <label htmlFor="khatabook-amount">Amount</label>
                    <AmountInput
                      id="khatabook-amount"
                      value={amount}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (/^\d{0,10}(?:\.\d{0,2})?$/.test(next)) setAmount(next);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !saving) handleAddEntry();
                      }}
                      placeholder="0.00"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9.]*"
                      maxLength={10}
                    />
                  </AmountField>
                  <PrimaryBtn onClick={handleAddEntry} disabled={saving}>
                    <FaPlus /> {saving ? "Saving…" : "Add entry"}
                  </PrimaryBtn>
                </AddRow>
              </EntryPanel>

              <LedgerWrap>
                <LedgerTable>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>Date</th>
                      <th>Notes</th>
                      <th className="number" style={{ width: 140 }}>Money in</th>
                      <th className="number" style={{ width: 140 }}>Money out</th>
                      <th className="action" style={{ width: 104 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((entry) => (
                      <tr key={entry.id}>
                        <td className="date">{formatEntryDate(entry)}</td>
                        <td className="notes">{entry.notes || entry.note || "No note"}</td>
                        <td className="number money-in">
                          {Number(entry.money_in) > 0 ? `+${fmtINR(entry.money_in)}` : "—"}
                        </td>
                        <td className="number money-out">
                          {Number(entry.money_out) > 0 ? `-${fmtINR(entry.money_out)}` : "—"}
                        </td>
                        <td className="action">
                          <DangerBtn title="Delete entry" onClick={() => handleDelete(entry.id)}>
                            <FaTrash /> Delete
                          </DangerBtn>
                        </td>
                      </tr>
                    ))}
                    {!ledger.length && (
                      <tr>
                        <td colSpan={5}>
                          <TableEmpty>
                            <FaWallet />
                            <strong>No transactions yet</strong>
                            <span>Add the first money in or money out entry above.</span>
                          </TableEmpty>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </LedgerTable>
              </LedgerWrap>
            </>
          ) : (
            <WelcomeState>
              <WelcomeIcon><FaWallet /></WelcomeIcon>
              <strong>Select a party</strong>
              <span>Choose someone from the left to view transactions and balance.</span>
            </WelcomeState>
          )}
        </Card>
      </Grid>
    </Page>
  );
};

export default KhatabookPage;

/* ---------------- styled ---------------- */
const Page = styled.div`
  --kh-green: #159447;
  --kh-green-soft: rgba(21, 148, 71, 0.1);
  --kh-red: #dc3636;
  --kh-red-soft: rgba(220, 54, 54, 0.1);
  background: var(--app-bg);
  color: var(--app-text);
  min-height: 100%;
  padding: clamp(12px, 2vw, 24px);
`;
const TopStrip = styled.div`
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(230px, 0.72fr) minmax(620px, 1.7fr);
  gap: clamp(18px, 2vw, 32px);
  align-items: center;
  border-radius: 22px;
  padding: clamp(20px, 2.6vw, 30px);
  background:
    radial-gradient(circle at 8% 10%, rgba(255, 255, 255, 0.16), transparent 34%),
    linear-gradient(125deg, var(--app-primary-dark), var(--app-primary) 72%, var(--app-primary-hover));
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  margin-bottom: 16px;
  box-shadow: 0 18px 38px rgba(var(--app-primary-rgb), 0.2);

  @media (max-width: 1180px) { grid-template-columns: 1fr; }
`;
const HeroCopy = styled.div`
  position: relative;
  z-index: 1;
`;
const Eyebrow = styled.div`
  margin-bottom: 5px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.72;
`;
const StripTitle = styled.h2`
  margin: 0;
  font-size: clamp(26px, 3vw, 38px);
  font-weight: 900;
  letter-spacing: -0.035em;
`;
const StripSub = styled.div`
  margin-top: 6px;
  max-width: 390px;
  font-size: 14px;
  line-height: 1.45;
  opacity: 0.82;
`;
const TopStats = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(138px, 1fr));

  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const TopStat = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: ${({ $tone }) => $tone === "green" ? "rgba(24, 190, 92, 0.13)" : $tone === "red" ? "rgba(255, 103, 103, 0.13)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 15px;
  padding: 12px;
  backdrop-filter: blur(8px);
`;
const StatIcon = styled.div`
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.14);
  font-size: 13px;
`;
const StatCopy = styled.div`
  min-width: 0;

  small {
    display: block;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 10px;
    font-weight: 800;
    opacity: 0.72;
  }
  strong {
    margin-top: 3px;
    display: block;
    overflow: hidden;
    font-size: clamp(14px, 1.35vw, 18px);
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #fff;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;
const Card = styled.div`
  min-width: 0;
  overflow: hidden;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.055);
  ${({ $ledger }) => $ledger && `padding: 18px;`}

  @media (min-width: 981px) {
    ${({ $sidebar }) => $sidebar && `position: sticky; top: 12px;`}
  }
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;
const SidebarHead = styled.div`
  padding: 18px 18px 12px;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const CardTitle = styled.h3`
  margin: 0;
  color: var(--app-text);
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;
const CardSub = styled.div`
  margin-top: 2px;
  color: var(--app-text-muted);
  font-size: 13px;
`;
const CountBadge = styled.span`
  min-width: 28px;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 900;
  text-align: center;
`;
const SearchBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  margin-top: 14px;
  padding: 11px 13px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-soft);
  color: var(--app-text-muted);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus-within {
    border-color: var(--app-primary);
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.12);
  }

  input {
    min-width: 0;
    flex: 1;
    border: 0;
    background: transparent;
    color: var(--app-text);
    outline: none;
    font: inherit;
  }
`;
const PartyList = styled.div`
  display: grid;
  gap: 6px;
  max-height: calc(100vh - 330px);
  min-height: 250px;
  padding: 4px 10px 12px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: var(--app-border); border-radius: 999px; }
  @media (max-width: 980px) { max-height: 380px; }
`;
const PartyRow = styled.button`
  width: 100%;
  min-width: 0;
  border: 1px solid ${({ $active }) => ($active ? "rgba(var(--app-primary-rgb), 0.34)" : "transparent")};
  border-radius: 13px;
  padding: 11px 12px;
  background: ${({ $active }) => ($active ? "var(--app-primary-soft)" : "transparent")};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "var(--app-primary-soft)" : "var(--app-surface-soft)")};
    transform: translateX(2px);
  }

  .left {
    min-width: 0;
    display: flex;
    gap: 11px;
    align-items: center;
  }
  .name {
    overflow: hidden;
    font-weight: 800;
    color: var(--app-text);
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    color: var(--app-text-muted);
    font-size: 12px;
  }
`;
const Avatar = styled.div`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: var(--app-primary-soft);
  color: ${COLOR.primary};
  display: grid;
  place-items: center;
  font-weight: 900;
`;
const PartyInfo = styled.div`
  min-width: 0;
`;
const PartyType = styled.span`
  max-width: 84px;
  overflow: hidden;
  padding: 2px 5px;
  border-radius: 5px;
  background: var(--app-surface-raised);
  color: var(--app-text-muted);
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Arrow = styled.div`
  color: var(--app-text-muted);
  font-size: 22px;
  font-weight: 900;
`;
const LedgerIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;
const LedgerAvatar = styled.div`
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 13px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 17px;
  font-weight: 900;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin: 0 0 14px;
`;
const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  background: ${({ $tone }) => $tone === "green" ? "var(--kh-green-soft)" : $tone === "red" ? "var(--kh-red-soft)" : "var(--app-surface-raised)"};
  border: 1px solid ${({ $tone }) => $tone === "green" ? "rgba(21, 148, 71, 0.2)" : $tone === "red" ? "rgba(220, 54, 54, 0.2)" : "var(--app-border)"};
  border-radius: 14px;
  padding: 13px;
  small {
    color: var(--app-text-muted);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  strong {
    display: block;
    margin-top: 4px;
    color: ${({ $tone }) => $tone === "green" ? "var(--kh-green)" : $tone === "red" ? "var(--kh-red)" : "var(--app-text)"};
    font-size: 18px;
    line-height: 1.1;
  }
`;
const SummaryIcon = styled.div`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  background: var(--app-surface);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
`;

const PrimaryBtn = styled.button`
  min-height: 44px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid var(--app-primary);
  background: var(--app-primary);
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;
  &:hover:not(:disabled) {
    background: var(--app-primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(var(--app-primary-rgb), 0.22);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
const RefreshBtn = styled(PrimaryBtn)`
  min-height: 40px;
  padding: 8px 12px;
  background: var(--app-surface);
  color: var(--app-primary);
  &:hover:not(:disabled) { color: #fff; }
  .spinning { animation: kh-spin 0.8s linear infinite; }
  @keyframes kh-spin { to { transform: rotate(360deg); } }
`;
const DangerBtn = styled(PrimaryBtn)`
  min-height: 34px;
  padding: 7px 10px;
  border-color: rgba(220, 54, 54, 0.16);
  background: var(--kh-red-soft);
  color: var(--kh-red);
  font-size: 12px;
  &:hover:not(:disabled) {
    border-color: var(--kh-red);
    background: var(--kh-red);
    color: #fff;
    box-shadow: 0 8px 18px rgba(220, 54, 54, 0.18);
  }
`;

const EntryPanel = styled.div`
  margin-bottom: 14px;
  padding: 13px;
  border: 1px solid var(--app-border);
  border-radius: 15px;
  background: var(--app-surface-raised);
`;
const EntryPanelHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 10px;
  strong { color: var(--app-text); font-size: 14px; }
  span { color: var(--app-text-muted); font-size: 12px; }
  @media (max-width: 600px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 2px;
  }
`;
const AddRow = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, 1.15fr) minmax(180px, 1fr) minmax(130px, 0.55fr);
  gap: 10px;
  align-items: end;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
const FlowTabs = styled.div`
  min-height: 44px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  padding: 4px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-soft);
`;
const FlowTab = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 7px;
  border: 1px solid ${({ $active, $tone }) => $active ? ($tone === "green" ? "rgba(21, 148, 71, 0.2)" : "rgba(220, 54, 54, 0.2)") : "transparent"};
  background: ${({ $active, $tone }) => $active ? ($tone === "green" ? "var(--kh-green-soft)" : "var(--kh-red-soft)") : "transparent"};
  color: ${({ $active, $tone }) => $active ? ($tone === "green" ? "var(--kh-green)" : "var(--kh-red)") : "var(--app-text-muted)"};
  font-weight: 800;
  padding: 8px 12px;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.16s ease;
`;
const AmountField = styled.div`
  display: grid;
  gap: 5px;
  label {
    padding-left: 2px;
    color: var(--app-text-muted);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;
const AmountInput = styled.input`
  min-height: 44px;
  box-sizing: border-box;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 10px 13px;
  background: var(--app-surface);
  color: var(--app-text);
  outline: none;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  &:focus {
    border-color: var(--app-primary);
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.12);
  }
`;
const LedgerWrap = styled.div`
  width: 100%;
  max-height: calc(100vh - 390px);
  overflow: auto;
  border: 1px solid var(--app-border);
  border-radius: 15px;
  &::-webkit-scrollbar { width: 7px; height: 7px; }
  &::-webkit-scrollbar-thumb { background: var(--app-border); border-radius: 999px; }
`;

const LedgerTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  color: var(--app-text);
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid var(--app-border);
    text-align: left;
  }
  thead {
    position: sticky;
    z-index: 2;
    top: 0;
    background: var(--app-surface-soft);
  }
  th {
    color: var(--app-text-muted);
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  td { font-size: 13px; }
  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover { background: var(--app-surface-raised); }
  .date { color: var(--app-text); font-weight: 650; white-space: nowrap; }
  .notes { color: var(--app-text-muted); }
  .number { text-align: right; white-space: nowrap; }
  .action { text-align: right; }
  .money-in { color: var(--kh-green); font-weight: 900; }
  .money-out { color: var(--kh-red); font-weight: 900; }
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--app-text-muted);
  padding: 32px 14px;
  font-size: 13px;
`;
const TableEmpty = styled.div`
  min-height: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  color: var(--app-text-muted);
  svg { margin-bottom: 4px; color: var(--app-primary); font-size: 24px; opacity: 0.7; }
  strong { color: var(--app-text); font-size: 14px; }
  span { font-size: 12px; }
`;
const WelcomeState = styled.div`
  min-height: 410px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: var(--app-text-muted);
  strong { margin-top: 14px; color: var(--app-text); font-size: 18px; }
  span { max-width: 340px; margin-top: 5px; font-size: 13px; line-height: 1.5; }
`;
const WelcomeIcon = styled.div`
  width: 68px;
  height: 68px;
  display: grid;
  place-items: center;
  border-radius: 22px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 26px;
`;
